import type { Components } from '@/api/generated/api';
import type { InfiniteData } from '@tanstack/vue-query';
import type { IObservationTimelineEntity } from '@/api/resources/observationTimeline.api';
import { getTimelineEntryKey, getTimelineRowKey } from './observationTimeline.utils';

export const retainFirstObservationTimelinePage = <TPage>(
  data: InfiniteData<TPage, number> | undefined,
): InfiniteData<TPage, number> | undefined => {
  const firstPage = data?.pages[0];
  if (!data || !firstPage || data.pages.length === 1) return data;

  return {
    ...data,
    pages: [firstPage],
    pageParams: [data.pageParams[0] ?? 1],
  };
};
export type TObservationTimelineCache = InfiniteData<
  Components.Schemas.PaginatedObservationTimelineSummaryResponseDto,
  number
>;

export const hasObservationTimelineEntity = (
  data: TObservationTimelineCache | undefined,
  entity: IObservationTimelineEntity,
): boolean =>
  data?.pages.some((page) =>
    page.rows.some((row) => getTimelineRowKey(row) === getTimelineEntryKey(entity.type, entity.id)),
  ) ?? false;

/** Replace or remove an existing card; insertion and ordering stay server-owned. */
export const updateObservationTimelineEntity = (
  data: TObservationTimelineCache | undefined,
  entity: IObservationTimelineEntity,
  summary: Components.Schemas.ObservationTimelineSummaryDto | null,
): TObservationTimelineCache | undefined => {
  if (!hasObservationTimelineEntity(data, entity) || !data) return data;
  const key = getTimelineEntryKey(entity.type, entity.id);
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      count: summary ? page.count : Math.max(0, page.count - 1),
      rows: page.rows.flatMap((row) => {
        if (getTimelineRowKey(row) !== key) return [row];
        return summary ? [summary] : [];
      }),
    })),
  };
};
